<!--
  app/components/pages/how-to-play/HtpSectionTimeline.vue
-->
<script setup lang="ts">
import { useNuxtApp } from "#imports";
import ContextLogoCrown from "~/components/context-shared/ContextLogoCrown.vue";
import HtpIconMatchball from "./HtpIconMatchball.vue";

const { $t } = useNuxtApp();
const { isQueens } = useWebContext();

const primaryColor = computed(() => (isQueens ? "#6EC3BD" : "#F4AF23"));

const timelineSegments = computed(() => [
  {
    key: "start",
    minutes: 5,
    color: primaryColor.value,
    labelKey: "howToPlay.timeline.scaleUp",
    labelColor: primaryColor.value,
    icon: null as string | null,
    stepKey: "start",
    crownLabel: "5'" as string | null,
  },
  {
    key: "secret1",
    minutes: 10,
    color: "#3caa36",
    labelKey: "howToPlay.timeline.secretCards",
    labelColor: "#3caa36",
    icon: "card" as string | null,
    stepKey: "secretCard",
    crownLabel: "17'" as string | null,
  },
  {
    key: "double",
    minutes: 8,
    color: "#f97316",
    labelKey: "howToPlay.timeline.goalX2",
    labelColor: "#f97316",
    icon: "ball" as string | null,
    stepKey: "doubleGoals",
    crownLabel: "20'" as string | null,
  },
  {
    key: "half",
    minutes: 8,
    color: "#ffffff",
    labelKey: "howToPlay.timeline.dice",
    labelColor: "#d1d5db",
    icon: "dice" as string | null,
    stepKey: "halfTime",
    crownLabel: "23'" as string | null,
  },
  {
    key: "secret2",
    minutes: 10,
    color: "#3caa36",
    labelKey: "howToPlay.timeline.secretCards",
    labelColor: "#3caa36",
    icon: "card" as string | null,
    stepKey: "secretCardReturn",
    crownLabel: "36'" as string | null,
  },
  {
    key: "matchball",
    minutes: 5,
    color: "#f97316",
    labelKey: "howToPlay.timeline.matchball",
    labelColor: "#f97316",
    icon: null as string | null,
    stepKey: "matchball",
    crownLabel: null,
  },
]);

const crownMarkers = computed(() => {
  const total = timelineSegments.value.reduce((sum, s) => sum + s.minutes, 0);
  let acc = 0;
  const result: { key: string; minute: string; leftPct: number }[] = [];
  timelineSegments.value.slice(0, -1).forEach(seg => {
    acc += seg.minutes;
    if (seg.crownLabel) {
      result.push({ key: seg.crownLabel, minute: seg.crownLabel, leftPct: (acc / total) * 100 });
    }
  });
  return result;
});

const topLabels = computed(() => [
  { key: "kickoff", leftPct: 0 },
  { key: "halftime", leftPct: crownMarkers.value.find(c => c.minute === "20'")?.leftPct ?? 50 },
]);

const iconSrcMap: Record<string, string> = {
  card: "/img/htp/timeline/cards.png",
  ball: "/img/htp/timeline/ball.png",
  dice: "/img/htp/timeline/dice.png",
};

const iconMarkers = computed(() => {
  const segs = timelineSegments.value;
  const total = segs.reduce((sum, s) => sum + s.minutes, 0);
  let acc = 0;
  return segs
    .map(seg => {
      const start = acc;
      acc += seg.minutes;
      if (!seg.icon) return null;
      return {
        key: seg.key,
        src: iconSrcMap[seg.icon],
        leftPct: ((start + seg.minutes / 2) / total) * 100,
        stepKey: seg.stepKey,
      };
    })
    .filter(Boolean) as { key: string; src: string; leftPct: number; stepKey: string }[];
});

const matchSteps = [
  {
    key: "kickoff",
    minute: "0'",
    icon: "crown",
    isMatchball: false,
    image: "/img/htp/timeline/cage.png",
    imageFit: "contain" as const,
  },
  {
    key: "start",
    minute: "0'–5'",
    icon: "crown",
    isMatchball: false,
    image: "/img/htp/steps/start.png",
    imageFit: "cover" as const,
  },
  {
    key: "secretCard",
    minute: "5'–17'",
    icon: "card",
    isMatchball: false,
    image: "/img/htp/steps/secret-card.png",
    imageFit: "cover" as const,
  },
  {
    key: "doubleGoals",
    minute: "17'–20'",
    icon: "ball",
    isMatchball: false,
    image: "/img/htp/steps/double-goals.png",
    imageFit: "cover" as const,
  },
  {
    key: "halfTime",
    minute: "20'–23'",
    icon: "dice",
    isMatchball: false,
    image: null,
    imageFit: "cover" as const,
  },
  {
    key: "secretCardReturn",
    minute: "23'–36'",
    icon: "card",
    isMatchball: false,
    image: "/img/htp/steps/secret-card-return.png",
    imageFit: "cover" as const,
  },
  {
    key: "matchball",
    minute: "36'",
    icon: "matchball",
    isMatchball: true,
    image: "/img/htp/steps/matchball.png",
    imageFit: "cover" as const,
  },
];

const scaleUpSteps = [1, 2, 3, 4, 5, 6, 7];
const scaleDownSteps = [5, 4, 3, 2, 1];

const hoveredGroup = ref<string | null>(null);
const activeStep = ref<string | null>(null);
let clearActiveTimeout: ReturnType<typeof setTimeout> | null = null;

function scrollToStep(key: string | null) {
  if (!key) return;
  if (clearActiveTimeout) clearTimeout(clearActiveTimeout);
  activeStep.value = key;
  const el = document.getElementById(`step-${key}`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  clearActiveTimeout = setTimeout(() => {
    activeStep.value = null;
  }, 3000);
}
</script>

<template>
  <section id="minute-by-minute" class="scroll-mt-48">
    <div
      class="flex flex-col bigsm:flex-row bigsm:items-baseline gap-1 bigsm:gap-4 mb-6 pb-2 bigsm:border-b bigsm:border-gray-700"
    >
      <div
        class="flex items-baseline gap-3 shrink-0 border-b border-gray-700 pb-2 bigsm:pb-0 bigsm:border-none"
      >
        <span class="text-primary shrink-0 self-center"><ContextLogoCrown :width="28" :height="28" /></span>
        <h2 class="font-kings text-3xl bigsm:text-4xl uppercase">
          {{ $t("howToPlay.section.timeline.title") }}
        </h2>
      </div>
      <p class="text-gray-400 text-sm ps-6 bigsm:ps-0 pt-2 bigsm:pt-0">
        {{ $t("howToPlay.section.timeline.subtitle") }}
      </p>
    </div>

    <!-- ─── Timeline bar ─────────────────────────────────────────────────── -->
    <div
      class="overflow-x-hidden bigsm:overflow-x-auto overflow-y-hidden pb-2 mb-14 -mx-4 px-4 bigsm:-mx-6 bigsm:px-6"
    >
      <div class="px-4 bigsm:min-w-[620px] max-w-5xl mx-auto">
        <NuxtImg
          src="/img/htp/timeline/match-timeline.png"
          :alt="$t(`howToPlay.section.timeline.title`) as string"
          width="1440"
          height="358"
          format="webp"
          loading="lazy"
          class="block h-auto w-full object-contain mb-2"
        />
      </div>
    </div>

    <!-- ─── Step details: vertical list ─────────────────────────────────── -->
    <ol class="space-y-0">
      <li
        v-for="(step, index) in matchSteps"
        :id="`step-${step.key}`"
        :key="step.key"
        class="flex gap-4 bigsm:gap-8 scroll-mt-48"
      >
        <!-- Left: minute + circle/icon + connector -->
        <div class="flex flex-col items-center shrink-0 w-10 bigsm:w-16">
          <!-- Minute label -->
          <span
            class="font-kings text-xs leading-none text-center mb-1 whitespace-nowrap"
            :class="step.isMatchball ? 'text-white/70' : 'text-white/50'"
            >{{ step.minute }}</span
          >

          <!-- Matchball -->
          <HtpIconMatchball
            v-if="step.isMatchball"
            :width="40"
            :height="40"
            class="shrink-0 transition-all duration-300 bigsm:w-[52px] bigsm:h-[52px]"
            :class="activeStep === step.key ? 'scale-110 mt-2' : ''"
          />

          <!-- Other: bordered circle with icon -->
          <div
            v-else
            class="w-9 h-9 bigsm:w-12 bigsm:h-12 rounded-full border-2 bg-gray-800 border-gray-700 flex items-center justify-center shrink-0 transition-all duration-300"
            :class="
              activeStep === step.key
                ? 'ring-2 ring-primary/60 ring-offset-2 ring-offset-black scale-110 mt-2'
                : ''
            "
          >
            <span v-if="step.icon === 'crown'" class="text-primary">
              <ContextLogoCrown :width="18" :height="18" />
            </span>
            <img
              v-else-if="step.icon === 'card'"
              src="/img/htp/timeline/cards.png"
              alt=""
              aria-hidden="true"
              class="h-7 bigsm:h-9 w-auto object-contain"
            />
            <img
              v-else-if="step.icon === 'ball'"
              src="/img/htp/timeline/ball.png"
              alt=""
              aria-hidden="true"
              class="h-7 bigsm:h-9 w-auto object-contain"
            />
            <img
              v-else-if="step.icon === 'dice'"
              src="/img/htp/timeline/dice.png"
              alt=""
              aria-hidden="true"
              class="h-7 bigsm:h-9 w-auto object-contain"
            />
          </div>

          <!-- Connector line -->
          <div
            v-if="index < matchSteps.length - 1"
            class="w-px flex-1 bg-gray-700 my-1.5 bigsm:my-2 min-h-6"
          />
        </div>

        <!-- Right: content card -->
        <div
          class="flex-1 min-w-0 mb-4 bigsm:mb-8 rounded-lg overflow-hidden bg-gray-900 transition-all duration-300"
          :class="activeStep === step.key ? 'ring-1 ring-primary/30' : ''"
        >
          <!-- Card content (not used for matchball, see below) -->
          <div v-if="!step.isMatchball" class="flex flex-col sm:flex-row sm:gap-5">
            <!-- Image: top (mobile/tablet) -->
            <div v-if="step.image" class="w-full aspect-[16/9] sm:hidden overflow-hidden">
              <img
                :src="step.image"
                alt=""
                aria-hidden="true"
                :class="[
                  'w-full h-full',
                  step.imageFit === 'contain' ? 'object-contain p-4' : 'object-cover',
                ]"
              />
            </div>
            <div class="p-5 bigsm:p-7 flex-1 min-w-0">
              <h3 class="bigsm:text-lg mb-1.5 bigsm:mb-2 text-white font-semibold">
                {{ $t(`howToPlay.section.${step.key}.title`) }}
              </h3>
              <p class="text-gray-300 leading-relaxed text-sm bigsm:text-base">
                {{ $t(`howToPlay.section.${step.key}.description`) }}
              </p>
              <!-- scaleUp: 1v1 → 7v7 -->
              <div
                v-if="step.key === 'start'"
                class="hidden sm:flex py-4 bigsm:pb-6 flex-nowrap items-center gap-1 bigsm:gap-2"
              >
                <template v-for="(n, idx) in scaleUpSteps" :key="n">
                  <div
                    class="flex items-center justify-center bg-gray-800 border rounded px-1.5 py-1 bigsm:rounded-lg bigsm:px-2.5 bigsm:py-1.5 shrink-0"
                    :class="n === 7 ? 'border-primary/40' : 'border-gray-700'"
                  >
                    <span
                      class="font-kings text-xs bigsm:text-base leading-none"
                      :class="n === 7 ? 'text-primary' : 'text-white'"
                      >{{ n }}v{{ n }}</span
                    >
                  </div>
                  <span
                    v-if="idx < scaleUpSteps.length - 1"
                    class="font-kings text-gray-600 text-xs bigsm:text-sm shrink-0 rtl:-scale-x-100"
                    >→</span
                  >
                </template>
              </div>
            </div>
            <!-- Image: right on desktop -->
            <div v-if="step.image" class="hidden sm:flex shrink-0 w-1/3 p-6 ps-0 items-start justify-center">
              <img
                :src="step.image"
                alt=""
                aria-hidden="true"
                class="w-full h-auto object-contain rounded-lg"
              />
            </div>
          </div>

          <!-- Dice outcomes -->
          <div v-if="step.key === 'halfTime'" class="px-6 pb-6 grid grid-cols-3 gap-3">
            <div class="flex flex-col items-center gap-2">
              <img
                src="/img/htp/dice/face-kings.png"
                alt=""
                aria-hidden="true"
                class="w-10 h-10 bigsm:w-20 bigsm:h-20 object-contain"
              />
              <span class="font-kings text-xs text-center leading-tight">
                <span class="text-primary">{{ $t("howToPlay.section.halfTime.dice.kingsLogo") }}</span> <br />
                <span class="text-white"> {{ $t("howToPlay.section.halfTime.dice.kingsLogoSuffix") }}</span>
              </span>
            </div>
            <div class="flex flex-col items-center gap-2">
              <img
                src="/img/htp/dice/2.png"
                alt=""
                aria-hidden="true"
                class="w-10 h-10 bigsm:w-20 bigsm:h-20 object-contain"
              />
              <span class="font-kings text-xs text-center text-primary leading-tight">{{
                $t("howToPlay.section.halfTime.dice.two")
              }}</span>
            </div>
            <div class="flex flex-col items-center gap-2">
              <img
                src="/img/htp/dice/3.png"
                alt=""
                aria-hidden="true"
                class="w-10 h-10 bigsm:w-20 bigsm:h-20 object-contain"
              />
              <span class="font-kings text-xs text-center text-primary leading-tight">{{
                $t("howToPlay.section.halfTime.dice.three")
              }}</span>
            </div>
          </div>

          <!-- Matchball -->
          <div v-if="step.isMatchball" class="divide-y divide-gray-700">
            <div class="px-4 py-4">
              <h3
                class="text-xl bigsm:text-2xl uppercase font-bold"
                :style="{
                  background:
                    'linear-gradient(91.93deg, #8d6d27 -13.52%, #a98e53 14.37%, #e7d6b2 62.01%, #dcbb72 102.68%)',
                  '-webkit-background-clip': 'text',
                  '-webkit-text-fill-color': 'transparent',
                  'background-clip': 'text',
                }"
              >
                {{ $t(`howToPlay.section.${step.key}.title`) }}
              </h3>
            </div>

            <!-- Subsection: Shootout -->
            <span id="shootouts" class="block" />
            <div class="flex flex-col sm:flex-row sm:gap-5">
              <div class="w-full aspect-[16/9] sm:hidden overflow-hidden">
                <img
                  src="/img/htp/steps/shootout.png"
                  alt=""
                  aria-hidden="true"
                  class="w-full h-full object-cover"
                />
              </div>
              <div class="px-4 py-2 bigsm:p-7 flex-1 min-w-0">
                <h4 class="text-base bigsm:text-xl mb-2 text-white font-semibold">
                  {{ $t("howToPlay.section.shootouts.title") }}
                </h4>
                <p class="text-gray-300 text-sm bigsm:text-base">
                  {{ $t("howToPlay.section.shootouts.description") }}
                </p>
              </div>
              <div class="hidden sm:flex shrink-0 w-1/3 p-6 ps-0 items-start justify-center">
                <img
                  src="/img/htp/steps/shootout.png"
                  alt=""
                  aria-hidden="true"
                  class="w-full h-auto object-contain rounded-lg"
                />
              </div>
            </div>

            <!-- Subsection: Matchball Mode -->
            <div class="flex flex-col sm:flex-row sm:gap-5">
              <div v-if="step.image" class="w-full aspect-[16/9] sm:hidden overflow-hidden">
                <img :src="step.image" alt="" aria-hidden="true" class="w-full h-full object-cover" />
              </div>
              <div class="px-4 py-2 bigsm:p-7 flex-1 min-w-0">
                <h4 class="text-base bigsm:text-xl mb-2 text-white font-semibold">
                  {{ $t(`howToPlay.section.${step.key}.subtitle`) }}
                </h4>
                <p class="text-gray-300 text-sm bigsm:text-base">
                  {{ $t(`howToPlay.section.${step.key}.description`) }}
                </p>
                <!-- Scale-Down: 5v5 → 1v1 -->
                <div class="py-4 hidden sm:flex flex-wrap bigsm:flex-nowrap items-center gap-1 bigsm:gap-2">
                  <template v-for="(n, idx) in scaleDownSteps" :key="n">
                    <div
                      class="flex items-center justify-center bg-gray-800 border rounded px-1.5 py-1 bigsm:rounded-lg bigsm:px-2.5 bigsm:py-1.5 shrink-0"
                      :class="n === 1 ? 'border-primary/40' : 'border-gray-700'"
                    >
                      <span
                        class="font-kings text-xs bigsm:text-base leading-none"
                        :class="n === 1 ? 'text-primary' : 'text-white'"
                        >{{ n }}v{{ n }}</span
                      >
                    </div>
                    <span
                      v-if="idx < scaleDownSteps.length - 1"
                      class="font-kings text-gray-600 text-xs bigsm:text-sm shrink-0 rtl:-scale-x-100"
                      >→</span
                    >
                  </template>
                </div>
              </div>
              <div
                v-if="step.image"
                class="hidden sm:flex shrink-0 w-1/3 p-6 ps-0 items-start justify-center"
              >
                <img
                  :src="step.image"
                  alt=""
                  aria-hidden="true"
                  class="w-full h-auto object-contain rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </li>
    </ol>
  </section>
</template>
